/**
 * upload.ts — subida directa de archivos, según el proveedor que devuelve el API.
 *
 * Módulo compartido: lo usan admin-panel (logo de marca) y reports (panel
 * fotográfico) sin acoplarse entre features.
 *
 * Flujo: pedimos una firma a /uploads/firma (el secret nunca sale del API). La
 * respuesta viene etiquetada con `provider`:
 *   - cloudinary → POST multipart a Cloudinary → secure_url
 *   - r2         → PUT directo a la URL prefirmada → publicUrl
 */
import { httpPost } from "./http-client.js";

interface CloudinaryFirma {
  provider: "cloudinary";
  signature: string;
  timestamp: number;
  apiKey: string;
  cloudName: string;
  folder: string;
}

interface R2Firma {
  provider: "r2";
  url: string;
  method: "PUT";
  key: string;
  publicUrl: string;
}

type Firma = CloudinaryFirma | R2Firma;

/** Sube un archivo al proveedor activo y devuelve su URL https pública. */
export async function uploadImagen(file: File, folder: string): Promise<string> {
  const contentType = file.type || "application/octet-stream";
  const firma = await httpPost<Firma>("/uploads/firma", { folder, contentType });

  if (firma.provider === "r2") {
    const res = await fetch(firma.url, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });
    if (!res.ok) {
      throw new Error("No se pudo subir la imagen. Intenta de nuevo.");
    }
    return firma.publicUrl;
  }

  // cloudinary
  const form = new FormData();
  form.append("file", file);
  form.append("api_key", firma.apiKey);
  form.append("timestamp", String(firma.timestamp));
  form.append("signature", firma.signature);
  form.append("folder", firma.folder);

  const res = await fetch(`https://api.cloudinary.com/v1_1/${firma.cloudName}/image/upload`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    throw new Error("No se pudo subir la imagen. Intenta de nuevo.");
  }
  const data = (await res.json()) as { secure_url?: string };
  if (!data.secure_url) {
    throw new Error("La respuesta de la subida no incluyó la URL.");
  }
  return data.secure_url;
}
